// Real HTTP adapter for Notification Policy (PRD 17 알림 정책, FEATURE_049). Talks to admin-api:
//   GET  /admin/notification-policies  → every event in the fixed catalog
// Read-only viewer (PRD v1.5/v1.8 + ADM.039) — no write path. Selected over the mock when
// VITE_USE_MOCK_API=false (see notification-settings.adapter.ts). Maps the server error
// envelope { error: { code, message, fields, meta } } to NotificationSettingsApiError;
// a missing response (offline) becomes code 'NETWORK'.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';

import {
  NotificationSettingsApiError,
  type NotificationPolicy,
  type NotificationSettingsApi,
  type NotificationSettingsApiErrorCode,
  type SendingReference,
} from '../types/notification-settings.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
    meta?: Record<string, unknown>;
  };
}

function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  // No response object → request never reached the server (offline / DNS / CORS).
  if (!candidate.response) {
    throw new NotificationSettingsApiError('NETWORK', '네트워크 연결을 확인해주세요.');
  }
  const payload = candidate.response.data?.error;
  throw new NotificationSettingsApiError(
    (payload?.code as NotificationSettingsApiErrorCode) ?? 'UNKNOWN',
    payload?.message ?? '알 수 없는 오류가 발생했습니다.',
    { fields: payload?.fields, meta: payload?.meta },
  );
}

export const notificationSettingsHttpApi: NotificationSettingsApi = {
  async list(): Promise<NotificationPolicy[]> {
    try {
      const res = await httpClient.get<ApiEnvelope<NotificationPolicy[]>>(
        endpoints.notificationPolicies,
      );
      const data = res.data?.data;
      // The list view iterates this; a non-array body (wrong envelope, HTML, paged
      // wrapper) must surface as a typed load error rather than white-screen the page.
      if (!Array.isArray(data)) {
        throw new NotificationSettingsApiError('UNKNOWN', '알림 정책을 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      if (e instanceof NotificationSettingsApiError) throw e;
      mapError(e);
    }
  },

  async getSendingReference(): Promise<SendingReference> {
    try {
      const res = await httpClient.get<ApiEnvelope<SendingReference>>(
        endpoints.notificationSendingReference,
      );
      const data = res.data?.data;
      // Both recipient groups must be arrays; a malformed body surfaces as a typed error so the
      // reference section shows its own retry rather than breaking the policy table above it.
      if (!data || !Array.isArray(data.patientReceives) || !Array.isArray(data.staffReceives)) {
        throw new NotificationSettingsApiError('UNKNOWN', 'SMS·이메일 발신 참고 정보를 불러올 수 없습니다.');
      }
      return data;
    } catch (e) {
      if (e instanceof NotificationSettingsApiError) throw e;
      mapError(e);
    }
  },
};
